// src/features/chat/logic.ts

import { chatApi, userApi, userprofileApi, attachmentApi } from '@core/api';
import { GetUserprofileDefault } from '@core/api/endpoints/userprofile/get_userprofile_default';
import type {
  Conversation,
  ConversationMember,
  ConversationRole,
  ConversationType,
  CreateConversationRequest,
  AttachmentPreview,
  Presence,
  PresenceStatus,
} from '@core/types/chat';
import type { User } from '@core/types/user';
import type { Entity } from '@core/types/entity';
import { candidateFromSearchResult, mergeCandidates, type PickerCandidate } from '@/features/userprofile/pickerCandidates';

// ---------------------------------------------------------------------------
// Avatar helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  'hsl(239 84% 67%)',
  'hsl(330 81% 60%)',
  'hsl(38 92% 50%)',
  'hsl(160 84% 39%)',
  'hsl(217 91% 60%)',
  'hsl(258 90% 66%)',
  'hsl(0 84% 60%)',
  'hsl(174 84% 40%)',
];

export function getAvatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

// Token-derived tonal pairs for avatar initials — same hash → same tone, but
// drawn entirely from the theme's semantic palette so light/dark both work.
const AVATAR_TONE_CLASSES = [
  'bg-primary/15 text-primary',
  'bg-accent text-accent-foreground',
  'bg-muted text-foreground',
  'bg-warning/20 text-warning',
  'bg-destructive/15 text-destructive',
  'bg-secondary text-secondary-foreground',
];

export function getAvatarToneClass(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_TONE_CLASSES[Math.abs(hash) % AVATAR_TONE_CLASSES.length];
}

// ---------------------------------------------------------------------------
// Attachment helpers
// ---------------------------------------------------------------------------

export interface PendingAttachment {
  id: string;
  file: File;
  previewUrl?: string;
  progress: number;
  uploadId?: string;
  entityId?: string;
  error?: string;
}

export function isImageMimeType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

export function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toUpperCase() : '';
}

/** Upload a file via presigned URL flow: GetUploadUrl → PUT → ConfirmUpload */
export async function uploadAttachment(
  file: File,
  onProgress?: (progress: number) => void,
  metadata?: Record<string, string>,
): Promise<{ entityId: string }> {
  // Step 1: Get presigned upload URL
  const uploadRes = await attachmentApi.GetUploadUrl({
    file_name: file.name,
    mime_type: file.type || 'application/octet-stream',
    size_bytes: file.size,
    metadata,
  });

  const uploadUrl = uploadRes.upload_url?.url;
  if (!uploadUrl) throw new Error('No upload URL received');

  // Step 2: Upload file directly to storage
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);

    // XHR's setRequestHeader APPENDS rather than replaces when called twice
    // for the same header — so setting Content-Type both from
    // required_headers and again below produced "image/png, image/png" in
    // the request, which broke the MinIO signature (SignedHeaders includes
    // content-type) and the upload returned 403. Track which headers we
    // already set from the signed response, then only fill Content-Type
    // manually if the server didn't include it.
    const appliedHeaders = new Set<string>();
    if (uploadRes.required_headers) {
      for (const [key, value] of Object.entries(uploadRes.required_headers)) {
        xhr.setRequestHeader(key, value);
        appliedHeaders.add(key.toLowerCase());
      }
    }
    if (!appliedHeaders.has('content-type')) {
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
    }

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100));
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(`Upload failed: ${xhr.status}`));
    };
    xhr.onerror = () => reject(new Error('Upload network error'));
    xhr.send(file);
  });

  // Step 3: Confirm upload
  const confirmRes = await attachmentApi.ConfirmUpload({
    upload_id: uploadRes.upload_id,
  });

  const entityId = confirmRes.attachment?.entity?.id;
  if (!entityId) throw new Error('No attachment entity ID returned');

  onProgress?.(100);
  return { entityId };
}

/** Get download URL for an attachment */
export async function getAttachmentDownloadUrl(attachmentId: string): Promise<string> {
  const res = await attachmentApi.GetDownloadUrl(attachmentId);
  return res.download_url?.url ?? '';
}

/** Check if an attachment preview represents an image */
export function isImageAttachment(preview: AttachmentPreview): boolean {
  return isImageMimeType(preview.mime_type);
}

// --- Conversation listing ---

export async function loadConversations(): Promise<Conversation[]> {
  // REST only. The ws_callable variant of ListConversations doesn't
  // currently propagate tenant_id/platform_id through ws-gateway's
  // authCtx, so the gRPC call into chat-svc loses scope and returns
  // an empty page. Re-enable the WS path once the gateway forwards
  // full request context.
  const response = await chatApi.ListConversations();
  return response.conversations?.conversations ?? [];
}

/**
 * The people to offer before anything is typed in "new conversation".
 *
 * Derived from the conversation list you already have rather than from a
 * directory of the platform: whoever last spoke in a chat you are in is, by
 * definition, someone you talk to. One request, and it deliberately stops at
 * the last message's sender — walking every conversation's member list to find
 * the other side of each DM would be one request per row for a dropdown nobody
 * has typed into yet.
 */
export async function loadRecentPeople(
  currentProfileId: string | null,
  limit = 8,
): Promise<PickerCandidate[]> {
  let conversations: Conversation[];
  try {
    const res = await chatApi.ListConversations({
      pagination: { limit: 30, cursor: '', direction: 'forward' },
      sort: [],
    } as never);
    conversations = res.conversations?.conversations ?? [];
  } catch {
    // Suggestions are a convenience; failing to build them is not an error the
    // person opening the picker needs to hear about — they can still search.
    return [];
  }

  // Direct conversations first: those partners are the likeliest next pick.
  const direct = conversations.filter(c => c.type === 'CONVERSATION_TYPE_DIRECT');
  const rest = conversations.filter(c => c.type !== 'CONVERSATION_TYPE_DIRECT');

  const fromSender = (c: Conversation): PickerCandidate[] => {
    const sender = c.last_message?.sender;
    const id = sender?.entity?.id;
    if (!id || id === currentProfileId || !sender?.display_name?.trim()) return [];
    return [{ profileId: id, displayName: sender.display_name.trim(), secondary: '' }];
  };

  return mergeCandidates(
    direct.flatMap(fromSender),
    rest.flatMap(fromSender),
  ).slice(0, limit);
}

export async function loadUnreadCounts(): Promise<any | null> {
  try {
    return await chatApi.GetUnreadCounts();
  } catch {
    return null;
  }
}

// --- Conversation display resolution ---

export interface ConversationDisplay {
  name: string;
  avatarUrl: string;
  avatarColorSeed: string;
  isDirect: boolean;
  peerProfileId?: string;
}

/**
 * Resolve display name + avatar for a conversation.
 *
 * For DIRECT conversations we ALWAYS resolve from members (ignoring the
 * backend-stored `name`, which is unreliable — may be a raw UUID). We pick the
 * first member whose profile id differs from the current user's active profile.
 *
 * For GROUP/CHANNEL we use `conversation.name` directly.
 */
export function resolveConversationDisplay(
  conversation: Conversation,
  currentProfileId: string | null,
  members?: ConversationMember[] | null,
): ConversationDisplay {
  const isDirect = conversation.type === 'CONVERSATION_TYPE_DIRECT';

  // A DM can have a custom avatar set by either participant (e.g. user
  // uploaded an image via ChatEditPage). When present it should win over
  // the peer's profile avatar in every rendering surface — list, header,
  // messages view. Peer name is still resolved from members.
  const convAvatar = conversation.avatar_url?.trim() || '';

  if (isDirect) {
    const peer = (members ?? [])
      .find(m => m.profile?.entity?.id && m.profile.entity.id !== currentProfileId);
    const peerName = peer?.profile?.display_name?.trim();
    const peerAvatar = peer?.profile?.avatar ?? '';
    if (peerName) {
      return {
        name: peerName,
        avatarUrl: convAvatar || peerAvatar,
        avatarColorSeed: peerName,
        isDirect: true,
        peerProfileId: peer?.profile?.entity?.id,
      };
    }
    // Fallback: last-message sender if it's not the current user.
    const lastSender = conversation.last_message?.sender;
    const lastSenderId = lastSender?.entity?.id;
    if (lastSenderId && lastSenderId !== currentProfileId && lastSender?.display_name) {
      return {
        name: lastSender.display_name,
        avatarUrl: convAvatar || lastSender.avatar || '',
        avatarColorSeed: lastSender.display_name,
        isDirect: true,
        peerProfileId: lastSenderId,
      };
    }
    return {
      name: 'chat.directMessage',
      avatarUrl: convAvatar,
      avatarColorSeed: conversation.entity?.id ?? '?',
      isDirect: true,
    };
  }

  const name = conversation.name?.trim() || 'chat.untitled';
  return {
    name,
    avatarUrl: convAvatar,
    avatarColorSeed: name,
    isDirect: false,
  };
}

/**
 * Bulk-load members for direct conversations and enrich them with real profile
 * data (display_name / avatar) via userprofileApi.ListUserprofilesByIDs —
 * chat's ConversationMember.profile is a snapshot Actor that may lack names.
 */
export async function loadDirectConversationMembers(
  conversations: Conversation[],
): Promise<Map<string, ConversationMember[]>> {
  const direct = conversations.filter(
    c => c.type === 'CONVERSATION_TYPE_DIRECT' && c.entity?.id,
  );
  if (direct.length === 0) return new Map();

  // Parallel fetch: members for every direct conversation.
  const rawEntries = await Promise.all(
    direct.map(async (conv): Promise<[string, ConversationMember[]]> => {
      const id = conv.entity!.id;
      try {
        const res = await chatApi.ListConversationMembers(id);
        return [id, res.members?.members ?? []];
      } catch {
        return [id, []];
      }
    }),
  );

  // Collect every unique profile id so we can enrich in a single request.
  const profileIds = new Set<string>();
  for (const [, members] of rawEntries) {
    for (const m of members) {
      const pid = m.profile?.entity?.id;
      if (pid) profileIds.add(pid);
    }
  }

  const enrichment = new Map<string, { displayName: string; avatar: string }>();
  if (profileIds.size > 0) {
    try {
      const res = await userprofileApi.ListUserprofilesByIDs({ ids: Array.from(profileIds) });
      const profiles = res.userprofiles?.profiles ?? [];
      for (const p of profiles) {
        const pid = p.entity?.id;
        if (!pid) continue;
        const composedName =
          p.display_name?.trim()
          || [p.first_name, p.last_name].filter(Boolean).join(' ').trim()
          || p.username?.trim()
          || '';
        // Backend enrichProfilesWithAvatars packs the active avatar's full URL
        // into `avatar.title` and the thumbnail URL into `avatar.icon`
        // (see services/userprofile/internal/services/v1/list_userprofiles_by_ids.go).
        // Prefer thumbnail for list rendering, fall back to the full URL.
        const avatarUrl = p.avatar?.icon?.trim() || p.avatar?.title?.trim() || '';
        enrichment.set(pid, { displayName: composedName, avatar: avatarUrl });
      }
    } catch {
      // Silent fallback — resolver will use whatever Actor data we had.
    }
  }

  // Apply enrichment: produce new ConversationMember copies with filled-in
  // display_name / avatar so the resolver can read them.
  const entries: [string, ConversationMember[]][] = rawEntries.map(([id, members]) => {
    const enriched = members.map((m): ConversationMember => {
      const pid = m.profile?.entity?.id;
      const extra = pid ? enrichment.get(pid) : undefined;
      if (!m.profile || !extra) return m;
      return {
        ...m,
        profile: {
          ...m.profile,
          display_name: m.profile.display_name || extra.displayName,
          avatar: m.profile.avatar || extra.avatar,
        },
      };
    });
    return [id, enriched];
  });

  return new Map(entries);
}

// --- User search for member selection ---

/**
 * Person search for people-first surfaces (⌘K palette, empty-chat grid).
 * Server-side `SearchUserprofiles` matches username, display name and first /
 * last name with privacy projection, so results carry a profile id but never an
 * e-mail — exactly what a "who do you mean" list should show.
 */
export async function searchPeople(query: string, limit = 8): Promise<PickerCandidate[]> {
  const res = await userprofileApi.SearchUserprofiles({ q: query.trim(), limit });
  return (res.results?.results ?? []).map(candidateFromSearchResult);
}

/** The workspace people offered before anything is typed — same endpoint, no query. */
export async function loadWorkspacePeople(limit = 12): Promise<PickerCandidate[]> {
  return searchPeople('', limit);
}

/**
 * Open (or create, then open) the direct conversation with a person by their
 * PROFILE id — the id every people-search surface already has, so picking
 * someone never has to resolve user id → default profile first.
 * Throws on failure; callers surface the error inline.
 */
export async function openDirectConversation(profileId: string): Promise<string> {
  const otherProfile: Entity = {
    type_id: '',
    type_name: 'core.userprofile',
    id: profileId,
    platform_id: '',
    tenant_id: '',
    product_id: '',
  };
  const response = await chatApi.GetOrCreateDirectConversation({ other_profile: otherProfile });
  const id = response.conversation?.entity?.id;
  if (!id) throw new Error('Could not start the conversation. Please try again.');
  return id;
}

export async function searchUsers(query: string): Promise<User[]> {
  // ListUsers no longer accepts a free-text `search` param; fetch a page and
  // filter client-side (same approach as UserPicker).
  const response = await userApi.ListUsers({
    pagination: { limit: 50, cursor: '', direction: 'forward' },
  });
  const users = response.users?.users;
  const list = Array.isArray(users) ? users : [];
  const q = query.trim().toLowerCase();
  if (!q) return list;
  return list.filter(
    (u) =>
      (u.email || '').toLowerCase().includes(q) ||
      (u.username || '').toLowerCase().includes(q),
  );
}

// --- Conversation creation ---

export type NewConversationType = 'direct' | 'group' | 'channel';

export interface CreateConversationData {
  type: NewConversationType;
  name: string;
  description: string;
  members: User[];
}

const TYPE_MAP: Record<NewConversationType, ConversationType> = {
  direct: 'CONVERSATION_TYPE_DIRECT',
  group: 'CONVERSATION_TYPE_GROUP',
  channel: 'CONVERSATION_TYPE_CHANNEL',
};

export function validateCreateConversation(
  data: CreateConversationData,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (data.type === 'direct') {
    if (data.members.length !== 1) {
      errors.members = 'chat.create.selectOnePerson';
    }
  } else {
    if (!data.name.trim()) {
      errors.name = 'chat.create.nameRequired';
    } else if (data.name.trim().length > 100) {
      errors.name = 'chat.create.nameTooLong';
    }
    if (data.type === 'group' && data.members.length < 1) {
      errors.members = 'chat.create.addAtLeastOneMember';
    }
  }

  return errors;
}

export async function createConversation(
  data: CreateConversationData,
): Promise<string> {
  if (data.type === 'direct') {
    const member = data.members[0];
    const userId = member.entity?.id;
    if (!userId) throw new Error('chat.create.missingMember');
    // The chat service expects the recipient's PROFILE id, not user id.
    // Resolve the recipient's default profile before creating/finding the DM.
    const profileResp = await GetUserprofileDefault(userId);
    const otherProfileEntity = profileResp.userprofile?.entity;
    if (!otherProfileEntity?.id) throw new Error('chat.create.noProfileForUser');
    const response = await chatApi.GetOrCreateDirectConversation({
      other_profile: otherProfileEntity,
    });
    return response.conversation?.entity?.id ?? '';
  }

  // Resolve each user to their default profile entity — chat service stores
  // members by profile id, not user id.
  const memberEntities: Entity[] = [];
  for (const u of data.members) {
    const uid = u.entity?.id;
    if (!uid) continue;
    try {
      const resp = await GetUserprofileDefault(uid);
      if (resp.userprofile?.entity) memberEntities.push(resp.userprofile.entity);
    } catch {
      // Skip users we can't resolve — they'd just be broken members anyway.
    }
  }

  const request: CreateConversationRequest = {
    type: TYPE_MAP[data.type],
    name: data.name.trim(),
    description: data.description.trim() || undefined,
    initial_members: memberEntities.length > 0
      ? { entities: memberEntities }
      : undefined,
  };

  const response = await chatApi.CreateConversation(request);
  return response.conversation?.entity?.id ?? '';
}

/**
 * Build a minimal `User` stub from a `UserPicker` selection (id + display
 * name only). `UserPicker` resolves to the underlying user id, same
 * convention as the calendar attendee picker — see
 * `frontend/src/components/UserPicker.tsx`. Keeps `createConversation` /
 * `addMember` unchanged: they already accept a `User` and only read
 * `.entity` and the display-name helpers below.
 */
export function memberFromPick(id: string, displayName: string): User {
  return {
    entity: { type_id: '', type_name: 'user', id, platform_id: '', tenant_id: '', product_id: '' },
    email: '',
    username: displayName,
    phone: '',
    email_verified: false,
    phone_verified: false,
    two_factor_enabled: false,
  };
}

export function getUserDisplayName(user: User): string {
  if (user.username) return user.username;
  if (user.email) return user.email;
  return user.entity?.id ?? '?';
}

export function getInitials(name: string): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0]?.toUpperCase() ?? '?';
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function formatRelativeTime(ts: unknown, locale = 'en'): string {
  if (!ts) return '';
  const date = typeof ts === 'object' && ts !== null && 'seconds' in ts
    ? new Date((ts as { seconds: number }).seconds * 1000)
    : new Date(ts as string | number);
  if (isNaN(date.getTime())) return '';

  const now = Date.now();
  const diffMs = now - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);

  // `narrow` reads as arithmetic in Russian — Intl renders -21 hours as "-21 ч",
  // which in a list of timestamps looks like a negative number rather than a
  // time. `short` says "21 ч назад" and stays compact in every locale we ship.
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'short' });

  if (diffSec < 60) return rtf.format(-diffSec, 'second');
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return rtf.format(-diffMin, 'minute');
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return rtf.format(-diffHr, 'hour');
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return rtf.format(-diffDay, 'day');
  if (diffDay < 30) return rtf.format(-Math.floor(diffDay / 7), 'week');

  return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
}

/** Absolute time for chat messages: "10:30", "Yesterday, 10:30", "Mar 8, 10:30" */
export function formatMessageTime(ts: unknown, locale = 'en'): string {
  if (!ts) return '';
  const date = typeof ts === 'object' && ts !== null && 'seconds' in ts
    ? new Date((ts as { seconds: number }).seconds * 1000)
    : new Date(ts as string | number);
  if (isNaN(date.getTime())) return '';

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today.getTime() - 86400000);
  const messageDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const timeStr = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(date);

  if (messageDay.getTime() === today.getTime()) {
    return timeStr;
  }
  if (messageDay.getTime() === yesterday.getTime()) {
    const rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' });
    const yesterdayLabel = rtf.format(-1, 'day');
    return `${yesterdayLabel}, ${timeStr}`;
  }
  if (date.getFullYear() === now.getFullYear()) {
    const dateStr = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(date);
    return `${dateStr}, ${timeStr}`;
  }
  return new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(date);
}

export function getLastMessagePreview(conversation: Conversation): {
  textKey?: string;
  text?: string;
  senderName?: string;
} {
  const msg = conversation.last_message;
  if (!msg) return { textKey: 'chat.noMessagesYet' };

  const senderName = msg.sender?.display_name ?? '';
  const content = msg.content?.slice(0, 100) ?? '';

  if (msg.attachments?.entities?.length) {
    return { textKey: 'chat.attachmentMessage', senderName };
  }

  return { text: content, senderName };
}

export const conversationTypeMap: Record<ConversationType, { iconKey: string; labelKey: string }> = {
  CONVERSATION_TYPE_UNSPECIFIED: { iconKey: 'message', labelKey: 'chat.type.conversation' },
  CONVERSATION_TYPE_DIRECT: { iconKey: 'user', labelKey: 'chat.type.direct' },
  CONVERSATION_TYPE_GROUP: { iconKey: 'users', labelKey: 'chat.type.group' },
  CONVERSATION_TYPE_CHANNEL: { iconKey: 'hash', labelKey: 'chat.type.channel' },
  CONVERSATION_TYPE_THREAD: { iconKey: 'thread', labelKey: 'chat.type.thread' },
};

export type ChatFilter = 'all' | 'direct' | 'group' | 'channel' | 'entity';

export const chatFilterToType: Record<ChatFilter, ConversationType | null> = {
  all: null,
  direct: 'CONVERSATION_TYPE_DIRECT',
  group: 'CONVERSATION_TYPE_GROUP',
  channel: 'CONVERSATION_TYPE_CHANNEL',
  entity: null, // entity-scoped conversations are filtered by parent, not type
};

export function filterConversations(
  conversations: Conversation[],
  search: string,
  typeFilter: ChatFilter,
): Conversation[] {
  let filtered = conversations;

  const mappedType = chatFilterToType[typeFilter];
  if (mappedType) {
    filtered = filtered.filter(c => c.type === mappedType);
  }
  if (typeFilter === 'entity') {
    filtered = filtered.filter((c: any) => c?.parent?.id);
  }

  if (search.trim()) {
    const q = search.toLowerCase();
    filtered = filtered.filter(c =>
      c.name?.toLowerCase().includes(q) ||
      c.description?.toLowerCase().includes(q),
    );
  }

  return filtered;
}

export function sortByLastActivity(conversations: Conversation[]): Conversation[] {
  return [...conversations].sort((a, b) => {
    const aTime = getTimestamp(a.last_activity_at) || getTimestamp(a.updated_at) || 0;
    const bTime = getTimestamp(b.last_activity_at) || getTimestamp(b.updated_at) || 0;
    return bTime - aTime;
  });
}

function getTimestamp(ts: unknown): number {
  if (!ts) return 0;
  if (typeof ts === 'object' && ts !== null && 'seconds' in ts) {
    return (ts as { seconds: number }).seconds * 1000;
  }
  const d = new Date(ts as string | number);
  return isNaN(d.getTime()) ? 0 : d.getTime();
}

// --- Conversation update ---

export interface UpdateConversationData {
  name: string;
  description: string;
  avatar_url: string;
}

export function validateUpdateConversation(
  data: UpdateConversationData,
): Record<string, string> {
  const errors: Record<string, string> = {};

  if (!data.name.trim()) {
    errors.name = 'chat.create.nameRequired';
  } else if (data.name.trim().length > 100) {
    errors.name = 'chat.create.nameTooLong';
  }

  return errors;
}

export async function loadConversation(id: string): Promise<Conversation> {
  const res = await chatApi.GetConversation(id);
  if (!res.conversation) throw new Error('Conversation not found');
  return res.conversation;
}

export async function updateConversation(
  id: string,
  conversation: Conversation,
  data: UpdateConversationData,
): Promise<Conversation> {
  const res = await chatApi.UpdateConversation(id, {
    conversation: {
      ...conversation,
      name: data.name.trim(),
      description: data.description.trim(),
      avatar_url: data.avatar_url.trim(),
    },
  });
  if (!res.conversation) throw new Error('Update failed');
  return res.conversation;
}

export async function updateConversationSettings(
  id: string,
  conversation: Conversation,
  settings: Conversation['settings'],
): Promise<Conversation> {
  const res = await chatApi.UpdateConversation(id, {
    conversation: {
      ...conversation,
      settings,
    },
  });
  if (!res.conversation) throw new Error('Update failed');
  return res.conversation;
}

// --- Members ---

export async function loadMembers(conversationId: string) {
  const res = await chatApi.ListConversationMembers(conversationId);
  return res.members?.members ?? [];
}

export async function addMember(conversationId: string, user: User) {
  await chatApi.AddConversationMember(conversationId, {
    profile: user.entity,
  });
}

export async function removeMember(conversationId: string, profileId: string) {
  await chatApi.RemoveConversationMember(conversationId, profileId);
}

export async function updateMemberRole(
  conversationId: string,
  profileId: string,
  role: ConversationRole,
) {
  await chatApi.UpdateConversationMemberRole(conversationId, profileId, { role });
}

// --- Conversation actions ---

export async function leaveConversation(conversationId: string) {
  await chatApi.LeaveConversation(conversationId, {});
}

export async function archiveConversation(conversationId: string) {
  await chatApi.ArchiveConversation(conversationId, { archive: true });
}

export async function deleteConversation(conversationId: string) {
  await chatApi.DeleteConversation(conversationId);
}

// --- Role display helpers ---

export const roleLabelKeyMap: Record<string, string> = {
  CONVERSATION_ROLE_OWNER: 'chat.role.owner',
  CONVERSATION_ROLE_ADMIN: 'chat.role.admin',
  CONVERSATION_ROLE_MODERATOR: 'chat.role.moderator',
  CONVERSATION_ROLE_MEMBER: 'chat.role.member',
  CONVERSATION_ROLE_VIEWER: 'chat.role.viewer',
};

export const roleBadgeVariantMap: Record<string, 'primary' | 'warning' | 'secondary' | 'default'> = {
  CONVERSATION_ROLE_OWNER: 'primary',
  CONVERSATION_ROLE_ADMIN: 'warning',
  CONVERSATION_ROLE_MODERATOR: 'secondary',
};

export const editableRoles = [
  { value: 'CONVERSATION_ROLE_ADMIN', labelKey: 'chat.role.admin' },
  { value: 'CONVERSATION_ROLE_MODERATOR', labelKey: 'chat.role.moderator' },
  { value: 'CONVERSATION_ROLE_MEMBER', labelKey: 'chat.role.member' },
  { value: 'CONVERSATION_ROLE_VIEWER', labelKey: 'chat.role.viewer' },
] as const;

export const threadPolicyOptions = [
  { value: 'THREAD_POLICY_ALLOWED', labelKey: 'chat.settings.threadAllowed' },
  { value: 'THREAD_POLICY_REQUIRED', labelKey: 'chat.settings.threadRequired' },
  { value: 'THREAD_POLICY_DISABLED', labelKey: 'chat.settings.threadDisabled' },
] as const;

// ---------------------------------------------------------------------------
// Presence (framework-adecd6 — GetPresence/UpdatePresence)
// ---------------------------------------------------------------------------

export const presenceStatusOptions = [
  { value: 'PRESENCE_STATUS_ONLINE', labelKey: 'chat.presence.online', dotClass: 'bg-success' },
  { value: 'PRESENCE_STATUS_AWAY', labelKey: 'chat.presence.away', dotClass: 'bg-warning' },
  { value: 'PRESENCE_STATUS_DND', labelKey: 'chat.presence.dnd', dotClass: 'bg-destructive' },
  { value: 'PRESENCE_STATUS_OFFLINE', labelKey: 'chat.presence.offline', dotClass: 'bg-muted-foreground/40' },
  { value: 'PRESENCE_STATUS_INVISIBLE', labelKey: 'chat.presence.invisible', dotClass: 'bg-muted-foreground/40' },
] as const;

/** Tailwind dot color for a presence status. Falls back to "unknown" (no dot). */
export function presenceDotClass(status?: PresenceStatus): string {
  return presenceStatusOptions.find(o => o.value === status)?.dotClass ?? '';
}

export function presenceLabelKey(status?: PresenceStatus): string {
  return presenceStatusOptions.find(o => o.value === status)?.labelKey ?? 'chat.presence.offline';
}

/**
 * Batch-fetch presence for a list of profile ids and index by profile id.
 * Swallows errors — presence is a nice-to-have indicator, never worth
 * blocking or erroring a page over.
 */
export async function loadPresences(profileIds: string[]): Promise<Map<string, Presence>> {
  const map = new Map<string, Presence>();
  const unique = Array.from(new Set(profileIds.filter(Boolean)));
  if (unique.length === 0) return map;
  try {
    const res = await chatApi.GetPresence({
      profiles: { entities: unique.map(id => ({ type_id: '', type_name: 'profile', id, platform_id: '', tenant_id: '', product_id: '' })) },
    });
    for (const p of res.presences?.items ?? []) {
      const id = p.profile?.id;
      if (id) map.set(id, p);
    }
  } catch {
    // silent — presence is best-effort
  }
  return map;
}

/** Update the current user's own presence. Best-effort, never throws. */
export async function updateOwnPresence(status: PresenceStatus, statusText?: string): Promise<void> {
  try {
    await chatApi.UpdatePresence({ status, status_text: statusText });
  } catch {
    // silent — presence updates are not user-facing critical actions
  }
}
